(() => {
  if (window.GGRProtectionManagerLoaded) return;
  window.GGRProtectionManagerLoaded = true;

  const ROOT_SELECTOR = '[data-ggr-protection-plan]';
  const MINIMUM_ELIGIBLE_SUBTOTAL = 3000;
  const CART_DRAWER_SECTION_ID = 'cart-drawer';

  const PLAN_HANDLES = new Set([
    'gamersgoretro-protection-plan-tier-1',
    'gamersgoretro-protection-plan-tier-2',
    'gamersgoretro-protection-plan-tier-3',
    'gamersgoretro-protection-plan-tier-4',
    'gamersgoretro-protection-plan-tier-5'
  ]);

  /*
   * Preserve Shopify's original fetch function.
   *
   * All protection-manager requests use originalFetch so they
   * aren't mistaken for customer-driven cart changes.
   */
  const originalFetch = window.fetch.bind(window);

  let actionInProgress = false;
  let syncTimer = null;
  let activeReminderRoot = null;

  const getRootUrl = () => {
    const value = window.Shopify?.routes?.root || '/';
    return value.endsWith('/') ? value : `${value}/`;
  };

  const buildUrl = (path) => {
    return `${getRootUrl()}${path}`;
  };

  const parseJson = async (response) => {
    return response.json().catch(() => ({}));
  };

  const request = async (path, options = {}) => {
    const response = await originalFetch(buildUrl(path), {
      ...options,
      headers: {
        Accept: 'application/json',
        ...(options.body
          ? { 'Content-Type': 'application/json' }
          : {}),
        ...(options.headers || {})
      }
    });

    const data = await parseJson(response);

    if (!response.ok) {
      throw new Error(
        data.description ||
          data.message ||
          'Unable to update the protection plan.'
      );
    }

    return data;
  };

  const getCart = () => {
    return request('cart.js', {
      method: 'GET',
      cache: 'no-store'
    });
  };

  const getPlanItems = (cart) => {
    return (cart?.items || []).filter((item) => {
      const handle =
        item.handle ||
        item.product_handle ||
        '';

      const markedAsProtection =
        item.properties?._ggr_protection_plan === true ||
        item.properties?._ggr_protection_plan === 'true';

      return (
        PLAN_HANDLES.has(handle) ||
        markedAsProtection
      );
    });
  };

  /*
   * Fetch a completely fresh server-rendered cart-drawer
   * section. This avoids relying on stale drawer footer HTML.
   */
  const fetchFreshProtectionState = async () => {
    const sectionUrl =
      `${getRootUrl()}?sections=${encodeURIComponent(
        CART_DRAWER_SECTION_ID
      )}&_ggr=${Date.now()}`;

    const response = await originalFetch(sectionUrl, {
      method: 'GET',
      headers: {
        Accept: 'application/json'
      },
      cache: 'no-store'
    });

    const sections = await parseJson(response);

    if (!response.ok) {
      throw new Error(
        'Unable to refresh the protection-plan information.'
      );
    }

    const html = sections[CART_DRAWER_SECTION_ID];

    if (typeof html !== 'string' || html.length === 0) {
      throw new Error(
        'Shopify did not return the cart-drawer section.'
      );
    }

    const documentFragment = new DOMParser().parseFromString(
      html,
      'text/html'
    );

    const root = documentFragment.querySelector(
      ROOT_SELECTOR
    );

    if (!root) {
      /*
       * The updated Liquid should always render this element.
       * Treat a missing element as an unknown state, not $0.
       */
      throw new Error(
        'Protection-plan cart data could not be found.'
      );
    }

    return {
      eligibleSubtotal: Number(
        root.dataset.eligibleSubtotal || 0
      ),
      desiredVariantId: Number(
        root.dataset.planVariantId || 0
      ),
      showOffer:
        root.dataset.showOffer === 'true'
    };
  };

  const getVisibleRoot = () => {
    const roots = Array.from(
      document.querySelectorAll(ROOT_SELECTOR)
    );

    if (!roots.length) return null;

    const openDrawer = Array.from(
      document.querySelectorAll('cart-drawer')
    ).find((drawer) => {
      return (
        drawer.classList.contains('active') ||
        drawer.classList.contains('open') ||
        drawer.classList.contains('is-open') ||
        drawer.hasAttribute('open')
      );
    });

    if (openDrawer) {
      const drawerRoot =
        openDrawer.querySelector(ROOT_SELECTOR);

      if (drawerRoot) return drawerRoot;
    }

    const visibleRoot = roots.find((root) => {
      return root.getClientRects().length > 0;
    });

    return visibleRoot || roots[roots.length - 1];
  };

  const setStatus = (root, message = '') => {
    const status = root?.querySelector(
      '[data-ggr-protection-status]'
    );

    if (status) {
      status.textContent = message;
    }
  };

  const setCardBusy = (root, busy) => {
    root
      ?.querySelectorAll(
        '[data-ggr-add-plan], [data-ggr-remove-plan]'
      )
      .forEach((button) => {
        button.disabled = busy;
      });
  };

  const setReminderBusy = (
    root,
    busy,
    message = ''
  ) => {
    const addButton = root?.querySelector(
      '[data-ggr-reminder-add]'
    );

    const continueButton = root?.querySelector(
      '[data-ggr-reminder-continue]'
    );

    const status = root?.querySelector(
      '[data-ggr-reminder-status]'
    );

    if (addButton) addButton.disabled = busy;
    if (continueButton) continueButton.disabled = busy;
    if (status) status.textContent = message;
  };

  const removePlanItems = async (planItems) => {
    if (!planItems.length) return;

    const updates = {};

    planItems.forEach((item) => {
      updates[item.key] = 0;
    });

    await request('cart/update.js', {
      method: 'POST',
      body: JSON.stringify({ updates })
    });
  };

  const addPlanVariant = async (variantId) => {
    if (!variantId) {
      throw new Error(
        'The correct protection-plan variant could not be found.'
      );
    }

    await request('cart/add.js', {
      method: 'POST',
      body: JSON.stringify({
        items: [
          {
            id: variantId,
            quantity: 1,
            properties: {
              Coverage:
                'Eligible games, consoles and accessories',
              'Coverage term': '12 months',
              _ggr_protection_plan: 'true'
            }
          }
        ]
      })
    });
  };

  const replacePlan = async (
    planItems,
    desiredVariantId
  ) => {
    if (planItems.length) {
      await removePlanItems(planItems);
    }

    await addPlanVariant(desiredVariantId);
  };

  const refreshPage = () => {
    window.location.reload();
  };

  /*
   * Synchronize an already-selected protection plan.
   *
   * This never automatically adds a plan when the customer
   * hasn't chosen one.
   */
  const synchronizeExistingPlan = async () => {
    if (actionInProgress) return;

    const [cart, freshState] = await Promise.all([
      getCart(),
      fetchFreshProtectionState()
    ]);

    const planItems = getPlanItems(cart);

    /*
     * No selected plan: nothing should be auto-added.
     */
    if (!planItems.length) return;

    /*
     * Existing plan must be removed when the actual freshly
     * rendered eligible subtotal is below $30.
     */
    if (
      freshState.eligibleSubtotal <
      MINIMUM_ELIGIBLE_SUBTOTAL
    ) {
      actionInProgress = true;

      try {
        await removePlanItems(planItems);
        refreshPage();
      } catch (error) {
        actionInProgress = false;

        console.error(
          'Unable to remove ineligible protection plan:',
          error
        );
      }

      return;
    }

    /*
     * Never replace or remove a plan merely because a valid
     * tier ID could not be loaded.
     */
    if (!freshState.desiredVariantId) return;

    const planIsCorrect =
      planItems.length === 1 &&
      Number(planItems[0].variant_id) ===
        freshState.desiredVariantId &&
      Number(planItems[0].quantity) === 1;

    if (planIsCorrect) return;

    /*
     * Correct upgrades, downgrades, duplicate plans or a plan
     * quantity greater than one.
     */
    actionInProgress = true;

    try {
      await replacePlan(
        planItems,
        freshState.desiredVariantId
      );

      refreshPage();
    } catch (error) {
      actionInProgress = false;

      console.error(
        'Unable to synchronize protection-plan tier:',
        error
      );
    }
  };

  const queueSynchronization = (delay = 350) => {
    window.clearTimeout(syncTimer);

    syncTimer = window.setTimeout(() => {
      synchronizeExistingPlan().catch((error) => {
        console.error(
          'Protection-plan synchronization failed:',
          error
        );
      });
    }, delay);
  };

  /*
   * Detect Shopify/Refresh cart mutations.
   *
   * Our own protection-manager requests bypass this wrapper by
   * using originalFetch directly.
   */
  const isCartMutationRequest = (resource) => {
    const url =
      typeof resource === 'string'
        ? resource
        : resource?.url || '';

    return [
      '/cart/add',
      '/cart/change',
      '/cart/update',
      '/cart/clear'
    ].some((part) => url.includes(part));
  };

  window.fetch = async (...argumentsList) => {
    const isCartMutation = isCartMutationRequest(
      argumentsList[0]
    );

    const response = await originalFetch(
      ...argumentsList
    );

    if (isCartMutation && !actionInProgress) {
      /*
       * The request has completed, so the cart state is current.
       * The section request gives us fresh Liquid-calculated
       * eligibility regardless of what Refresh redraws locally.
       */
      queueSynchronization(300);
    }

    return response;
  };

  const addProtectionFromCard = async (root) => {
    if (actionInProgress) return;

    actionInProgress = true;

    setCardBusy(root, true);
    setStatus(root, 'Adding protection plan…');

    try {
      const [cart, freshState] = await Promise.all([
        getCart(),
        fetchFreshProtectionState()
      ]);

      if (
        freshState.eligibleSubtotal <
          MINIMUM_ELIGIBLE_SUBTOTAL ||
        !freshState.desiredVariantId
      ) {
        throw new Error(
          'This cart does not currently qualify for a protection plan.'
        );
      }

      await replacePlan(
        getPlanItems(cart),
        freshState.desiredVariantId
      );

      refreshPage();
    } catch (error) {
      actionInProgress = false;

      setCardBusy(root, false);
      setStatus(root, error.message);

      console.error(
        'Protection-plan add failed:',
        error
      );
    }
  };

  const removeProtectionFromCard = async (root) => {
    if (actionInProgress) return;

    actionInProgress = true;

    setCardBusy(root, true);
    setStatus(root, 'Removing protection plan…');

    try {
      const cart = await getCart();
      await removePlanItems(getPlanItems(cart));
      refreshPage();
    } catch (error) {
      actionInProgress = false;

      setCardBusy(root, false);
      setStatus(root, error.message);

      console.error(
        'Protection-plan removal failed:',
        error
      );
    }
  };

  const openReminder = (root) => {
    const reminder = root?.querySelector(
      '[data-ggr-protection-reminder]'
    );

    if (!reminder) return false;

    activeReminderRoot = root;
    reminder.hidden = false;

    document.body.classList.add(
      'ggr-reminder-open'
    );

    window.setTimeout(() => {
      reminder
        .querySelector('[data-ggr-reminder-add]')
        ?.focus();
    }, 0);

    return true;
  };

  const closeReminder = (
    root = activeReminderRoot
  ) => {
    const reminder = root?.querySelector(
      '[data-ggr-protection-reminder]'
    );

    if (reminder) {
      reminder.hidden = true;
    }

    setReminderBusy(root, false, '');

    document.body.classList.remove(
      'ggr-reminder-open'
    );

    if (activeReminderRoot === root) {
      activeReminderRoot = null;
    }
  };

  const continueToCheckout = () => {
    window.location.assign(
      `${getRootUrl()}checkout`
    );
  };

  const addProtectionAndCheckout = async (root) => {
    if (!root || actionInProgress) return;

    actionInProgress = true;

    setReminderBusy(
      root,
      true,
      'Adding protection and continuing to checkout…'
    );

    try {
      const [cart, freshState] = await Promise.all([
        getCart(),
        fetchFreshProtectionState()
      ]);

      if (
        freshState.eligibleSubtotal <
          MINIMUM_ELIGIBLE_SUBTOTAL ||
        !freshState.desiredVariantId
      ) {
        throw new Error(
          'This cart no longer qualifies for protection.'
        );
      }

      await replacePlan(
        getPlanItems(cart),
        freshState.desiredVariantId
      );

      continueToCheckout();
    } catch (error) {
      actionInProgress = false;

      setReminderBusy(
        root,
        false,
        error.message
      );

      console.error(
        'Protection reminder add failed:',
        error
      );
    }
  };

  /*
   * Checkout interception.
   *
   * preventDefault is called synchronously before any request,
   * so Shopify cannot submit the checkout form before the
   * reminder decision is made.
   */
  const handleCheckoutClick = async (
    checkoutButton,
    event
  ) => {
    event.preventDefault();
    event.stopImmediatePropagation();

    try {
      const [cart, freshState] = await Promise.all([
        getCart(),
        fetchFreshProtectionState()
      ]);

      const planItems = getPlanItems(cart);

      /*
       * Existing selected protection:
       * correct its tier if necessary, then continue.
       */
      if (planItems.length) {
        if (
          freshState.eligibleSubtotal <
          MINIMUM_ELIGIBLE_SUBTOTAL
        ) {
          await removePlanItems(planItems);
          continueToCheckout();
          return;
        }

        if (freshState.desiredVariantId) {
          const planIsCorrect =
            planItems.length === 1 &&
            Number(planItems[0].variant_id) ===
              freshState.desiredVariantId &&
            Number(planItems[0].quantity) === 1;

          if (!planIsCorrect) {
            await replacePlan(
              planItems,
              freshState.desiredVariantId
            );
          }
        }

        continueToCheckout();
        return;
      }

      /*
       * Cart doesn't qualify, so there is no reminder.
       */
      if (
        !freshState.showOffer ||
        freshState.eligibleSubtotal <
          MINIMUM_ELIGIBLE_SUBTOTAL ||
        !freshState.desiredVariantId
      ) {
        continueToCheckout();
        return;
      }

      /*
       * Use the currently displayed cart drawer/page modal.
       */
      const root =
        checkoutButton
          .closest('cart-drawer')
          ?.querySelector(ROOT_SELECTOR) ||
        getVisibleRoot();

      if (!openReminder(root)) {
        /*
         * Never trap checkout if the modal markup cannot be found.
         */
        continueToCheckout();
      }
    } catch (error) {
      console.error(
        'Unable to verify protection status:',
        error
      );

      /*
       * Do not block a customer from checking out if Shopify's
       * verification request fails.
       */
      continueToCheckout();
    }
  };

  /*
   * Delegated click handling survives all AJAX drawer redraws.
   */
  document.addEventListener(
    'click',
    (event) => {
      const addButton = event.target.closest(
        '[data-ggr-add-plan]'
      );

      if (addButton) {
        event.preventDefault();
        event.stopImmediatePropagation();

        addProtectionFromCard(
          addButton.closest(ROOT_SELECTOR)
        );

        return;
      }

      const removeButton = event.target.closest(
        '[data-ggr-remove-plan]'
      );

      if (removeButton) {
        event.preventDefault();
        event.stopImmediatePropagation();

        removeProtectionFromCard(
          removeButton.closest(ROOT_SELECTOR)
        );

        return;
      }

      const reminderAddButton =
        event.target.closest(
          '[data-ggr-reminder-add]'
        );

      if (reminderAddButton) {
        event.preventDefault();
        event.stopImmediatePropagation();

        addProtectionAndCheckout(
          reminderAddButton.closest(ROOT_SELECTOR)
        );

        return;
      }

      const reminderContinueButton =
        event.target.closest(
          '[data-ggr-reminder-continue]'
        );

      if (reminderContinueButton) {
        event.preventDefault();
        event.stopImmediatePropagation();

        const root =
          reminderContinueButton.closest(
            ROOT_SELECTOR
          );

        closeReminder(root);
        continueToCheckout();

        return;
      }

      const reminderCloseButton =
        event.target.closest(
          '[data-ggr-close-reminder]'
        );

      if (reminderCloseButton) {
        event.preventDefault();
        event.stopImmediatePropagation();

        closeReminder(
          reminderCloseButton.closest(ROOT_SELECTOR)
        );

        return;
      }

      const checkoutButton = event.target.closest(
        'button[name="checkout"]'
      );

      if (checkoutButton) {
        handleCheckoutClick(
          checkoutButton,
          event
        );
      }
    },
    true
  );

  document.addEventListener('keydown', (event) => {
    if (
      event.key === 'Escape' &&
      activeReminderRoot
    ) {
      closeReminder(activeReminderRoot);
    }
  });

  /*
   * Fallback hooks for quantity/remove controls in case a
   * theme script performs a cart update without window.fetch.
   */
  document.addEventListener('click', (event) => {
    const cartControl = event.target.closest(
      [
        'cart-remove-button',
        '.cart-remove-button',
        '.quantity__button'
      ].join(',')
    );

    if (cartControl && !actionInProgress) {
      queueSynchronization(800);
    }
  });

  document.addEventListener('change', (event) => {
    if (
      !actionInProgress &&
      event.target.matches(
        'input[name="updates[]"], .quantity__input'
      )
    ) {
      queueSynchronization(800);
    }
  });

  [
    'cart:updated',
    'cart:update',
    'cart:refresh',
    'shopify:section:load'
  ].forEach((eventName) => {
    document.addEventListener(eventName, () => {
      if (!actionInProgress) {
        queueSynchronization(300);
      }
    });
  });
})();